import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocalization } from '../../../hooks/useLocalization';

/**
 * Ad Management Dashboard
 * Connected to /api/ads backend endpoints for real campaign & ad management.
 */

interface Campaign {
    _id: string;
    name: string;
    campaignType: string;
    platform: string;
    status: 'active' | 'paused' | 'draft' | 'ended';
    budget?: { daily?: number; total?: number };
    metrics?: { impressions?: number; clicks?: number; conversions?: number; spend?: number; ctr?: number };
    startDate?: string;
    endDate?: string;
    createdAt?: string;
}

interface Ad {
    _id: string;
    name: string;
    type: string;
    status: string;
    campaignId: string;
    metrics?: { impressions?: number; clicks?: number; conversions?: number };
    hotelId?: string;
    destinationId?: string;
}

interface AnalyticsSummary {
    totalImpressions: number;
    totalClicks: number;
    totalConversions: number;
    totalBookings: number;
    totalRevenue: number;
    averageCTR: string;
    averageConversionRate: string;
    topPerformingAds: any[];
}

interface Creative {
    id: string;
    name: string;
    size: string;
    template: string;
    headline: string;
    ctaText: string;
    description: string;
    bgColor: string;
    textColor: string;
    ctaColor: string;
    imageUrl: string;
    campaignId?: string;
    createdAt: string;
}

interface PlatformConfig {
    enabled: boolean;
    status: string;
    [key: string]: any;
}

interface PlatformSettings {
    google: PlatformConfig;
    meta: PlatformConfig;
    tiktok: PlatformConfig;
    microsoft: PlatformConfig;
}

const BANNER_SIZES = [
    { id: '300x250', label: 'Medium Rectangle', w: 300, h: 250 },
    { id: '728x90', label: 'Leaderboard', w: 728, h: 90 },
    { id: '160x600', label: 'Wide Skyscraper', w: 160, h: 600 },
    { id: '1200x628', label: 'Social Feed', w: 1200, h: 628 },
    { id: '320x50', label: 'Mobile Banner', w: 320, h: 50 },
];

const TEMPLATES = [
    { id: 'travel-promo', name: 'Travel Promo', bg: '#1e3a5f', text: '#ffffff', cta: '#f59e0b' },
    { id: 'beach-holiday', name: 'Beach Holiday', bg: '#0891b2', text: '#ffffff', cta: '#fbbf24' },
    { id: 'luxury-escape', name: 'Luxury Escape', bg: '#1c1917', text: '#d4af37', cta: '#d4af37' },
    { id: 'family-fun', name: 'Family Fun', bg: '#7c3aed', text: '#ffffff', cta: '#34d399' },
    { id: 'seasonal-sale', name: 'Seasonal Sale', bg: '#dc2626', text: '#ffffff', cta: '#fbbf24' },
    { id: 'minimal-clean', name: 'Minimal Clean', bg: '#ffffff', text: '#1f2937', cta: '#3b82f6' },
];

const AdManagementContent: React.FC = () => {
    const { language } = useLocalization();
    const [activeTab, setActiveTab] = useState<'overview' | 'campaigns' | 'ads' | 'creatives' | 'settings'>('overview');
    const [loading, setLoading] = useState(true);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [ads, setAds] = useState<Ad[]>([]);
    const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Creative designer state
    const [creatives, setCreatives] = useState<Creative[]>([]);
    const [showEditor, setShowEditor] = useState(false);
    const [editCreative, setEditCreative] = useState<Partial<Creative>>({
        name: '', size: '300x250', template: 'travel-promo',
        headline: 'Dream Vacation Awaits', ctaText: 'Book Now',
        description: 'Premium travel packages', bgColor: '#1e3a5f',
        textColor: '#ffffff', ctaColor: '#f59e0b', imageUrl: ''
    });
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // AI generation state
    const [aiGenerating, setAiGenerating] = useState<'headline' | 'description' | 'both' | null>(null);

    // Stock image search state
    const [showStockSearch, setShowStockSearch] = useState(false);
    const [stockQuery, setStockQuery] = useState('');
    const [stockResults, setStockResults] = useState<{ url: string; thumb: string; alt: string }[]>([]);
    const [stockLoading, setStockLoading] = useState(false);

    // Layer management state
    const [layers, setLayers] = useState<{ id: string; name: string; visible: boolean; opacity: number }[]>([
        { id: 'background', name: 'Background', visible: true, opacity: 100 },
        { id: 'image', name: 'Image', visible: true, opacity: 100 },
        { id: 'headline', name: 'Headline', visible: true, opacity: 100 },
        { id: 'description', name: 'Description', visible: true, opacity: 85 },
        { id: 'cta', name: 'CTA Button', visible: true, opacity: 100 },
    ]);
    const bgImageRef = useRef<HTMLImageElement | null>(null);

    // Platform settings state
    const [platformSettings, setPlatformSettings] = useState<PlatformSettings>({
        google: { enabled: false, clientId: '', apiKey: '', accountId: '', status: 'disconnected' },
        meta: { enabled: false, appId: '', appSecret: '', adAccountId: '', status: 'disconnected' },
        tiktok: { enabled: false, appId: '', secret: '', advertiserId: '', status: 'disconnected' },
        microsoft: { enabled: false, clientId: '', clientSecret: '', accountId: '', status: 'disconnected' }
    });
    const [savingSettings, setSavingSettings] = useState(false);
    const [settingsMsg, setSettingsMsg] = useState<string | null>(null);

    const t = (key: string) => {
        const translations: Record<string, { en: string; sv: string }> = {
            'ad_management': { en: 'Ad Management', sv: 'Annonshantering' },
            'overview': { en: 'Overview', sv: 'Översikt' },
            'campaigns': { en: 'Campaigns', sv: 'Kampanjer' },
            'ads': { en: 'Ads', sv: 'Annonser' },
            'creatives': { en: 'Creatives', sv: 'Annonsmaterial' },
            'settings': { en: 'Settings', sv: 'Inställningar' },
            'total_spend': { en: 'Total Spend', sv: 'Total kostnad' },
            'impressions': { en: 'Impressions', sv: 'Visningar' },
            'clicks': { en: 'Clicks', sv: 'Klick' },
            'conversions': { en: 'Conversions', sv: 'Konverteringar' },
            'ctr': { en: 'CTR', sv: 'CTR' },
            'revenue': { en: 'Revenue', sv: 'Intäkter' },
            'active': { en: 'Active', sv: 'Aktiv' },
            'paused': { en: 'Paused', sv: 'Pausad' },
            'draft': { en: 'Draft', sv: 'Utkast' },
            'ended': { en: 'Ended', sv: 'Avslutad' },
            'budget': { en: 'Budget', sv: 'Budget' },
            'spent': { en: 'Spent', sv: 'Spenderat' },
            'status': { en: 'Status', sv: 'Status' },
            'name': { en: 'Name', sv: 'Namn' },
            'platform': { en: 'Platform', sv: 'Plattform' },
            'type': { en: 'Type', sv: 'Typ' },
            'no_campaigns': { en: 'No campaigns yet. Create your first campaign to get started.', sv: 'Inga kampanjer ännu. Skapa din första kampanj.' },
            'no_ads': { en: 'No ads yet. Create ads within a campaign.', sv: 'Inga annonser ännu. Skapa annonser i en kampanj.' },
            'create_campaign': { en: 'Create Campaign', sv: 'Skapa kampanj' },
            'create_ad': { en: 'Create Ad', sv: 'Skapa annons' },
            'this_month': { en: 'This Month', sv: 'Denna månad' },
            'loading': { en: 'Loading ad data...', sv: 'Laddar annonsdata...' },
            'error_loading': { en: 'Could not load ad data', sv: 'Kunde inte ladda annonsdata' },
            'retry': { en: 'Retry', sv: 'Försök igen' },
            'total_campaigns': { en: 'Total Campaigns', sv: 'Totalt kampanjer' },
            'active_campaigns': { en: 'Active Campaigns', sv: 'Aktiva kampanjer' },
            'total_ads': { en: 'Total Ads', sv: 'Totalt annonser' },
            'top_performing': { en: 'Top Performing', sv: 'Bäst presterande' },
            'hotel_promotion': { en: 'Hotel Promotion', sv: 'Hotellkampanj' },
            'destination_promotion': { en: 'Destination Promotion', sv: 'Destinationskampanj' },
            'banner': { en: 'Banner', sv: 'Banner' },
            'search_result': { en: 'Search Result', sv: 'Sökresultat' },
            'internal': { en: 'Internal', sv: 'Intern' },
            'delete': { en: 'Delete', sv: 'Radera' },
            'pause': { en: 'Pause', sv: 'Pausa' },
            'activate': { en: 'Activate', sv: 'Aktivera' },
            // Creative designer
            'new_creative': { en: 'New Creative', sv: 'Nytt annonsmaterial' },
            'headline': { en: 'Headline', sv: 'Rubrik' },
            'cta_text': { en: 'CTA Text', sv: 'CTA-text' },
            'description': { en: 'Description', sv: 'Beskrivning' },
            'bg_color': { en: 'Background', sv: 'Bakgrund' },
            'text_color': { en: 'Text Color', sv: 'Textfärg' },
            'cta_color': { en: 'CTA Color', sv: 'CTA-färg' },
            'size': { en: 'Size', sv: 'Storlek' },
            'template': { en: 'Template', sv: 'Mall' },
            'preview': { en: 'Preview', sv: 'Förhandsvisa' },
            'save_creative': { en: 'Save Creative', sv: 'Spara annonsmaterial' },
            'saved_creatives': { en: 'Saved Creatives', sv: 'Sparade annonsmaterial' },
            'no_creatives': { en: 'No creatives yet. Design your first banner!', sv: 'Inga annonsmaterial ännu. Designa din första banner!' },
            'export_png': { en: 'Export PNG', sv: 'Exportera PNG' },
            'cancel': { en: 'Cancel', sv: 'Avbryt' },
            'ai_generate': { en: 'AI Generate', sv: 'AI-generera' },
            'ai_generating': { en: 'Generating...', sv: 'Genererar...' },
            'stock_search': { en: 'Stock Images', sv: 'Stockbilder' },
            'stock_search_placeholder': { en: 'Search travel images...', sv: 'Sök resebilder...' },
            'layers': { en: 'Layers', sv: 'Lager' },
            'layer_visible': { en: 'Visible', sv: 'Synlig' },
            'layer_hidden': { en: 'Hidden', sv: 'Dold' },
            'opacity': { en: 'Opacity', sv: 'Opacitet' },
            'bg_image': { en: 'Background Image', sv: 'Bakgrundsbild' },
            'remove_image': { en: 'Remove', sv: 'Ta bort' },
            // Settings
            'platform_settings': { en: 'Platform Settings', sv: 'Plattformsinställningar' },
            'api_key': { en: 'API Key', sv: 'API-nyckel' },
            'client_id': { en: 'Client ID', sv: 'Klient-ID' },
            'client_secret': { en: 'Client Secret', sv: 'Klienthemlighet' },
            'account_id': { en: 'Account ID', sv: 'Konto-ID' },
            'app_id': { en: 'App ID', sv: 'App-ID' },
            'app_secret': { en: 'App Secret', sv: 'App-hemlighet' },
            'advertiser_id': { en: 'Advertiser ID', sv: 'Annonsör-ID' },
            'connected': { en: 'Connected', sv: 'Ansluten' },
            'disconnected': { en: 'Disconnected', sv: 'Frånkopplad' },
            'enabled': { en: 'Enabled', sv: 'Aktiverad' },
            'disabled': { en: 'Disabled', sv: 'Inaktiverad' },
            'save_settings': { en: 'Save Settings', sv: 'Spara inställningar' },
            'settings_saved': { en: 'Settings saved successfully', sv: 'Inställningar sparade' },
        };
        return translations[key]?.[language as 'en' | 'sv'] || translations[key]?.en || key;
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [campaignsRes, adsRes, summaryRes, creativesRes, settingsRes] = await Promise.all([
                fetch('/api/ads/campaigns').catch(() => null),
                fetch('/api/ads/ads').catch(() => null),
                fetch('/api/ads/analytics/summary').catch(() => null),
                fetch('/api/ads/creatives').catch(() => null),
                fetch('/api/ads/settings').catch(() => null),
            ]);

            const campaignsData = campaignsRes?.ok ? await campaignsRes.json() : null;
            const adsData = adsRes?.ok ? await adsRes.json() : null;
            const summaryData = summaryRes?.ok ? await summaryRes.json() : null;
            const creativesData = creativesRes?.ok ? await creativesRes.json() : null;
            const settingsData = settingsRes?.ok ? await settingsRes.json() : null;

            setCampaigns(campaignsData?.campaigns || []);
            setAds(adsData?.ads || []);
            setSummary(summaryData?.summary || null);
            if (creativesData?.creatives) setCreatives(creativesData.creatives);
            if (settingsData?.settings) setPlatformSettings(settingsData.settings);
        } catch (err: any) {
            console.error('Failed to fetch ad data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleStatusChange = async (campaignId: string, newStatus: string) => {
        try {
            const res = await fetch(`/api/ads/campaigns/${campaignId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
            });
            if (res.ok) fetchData();
        } catch (err) {
            console.error('Status update failed:', err);
        }
    };

    const handleDeleteCampaign = async (campaignId: string) => {
        if (!confirm(language === 'sv' ? 'Radera denna kampanj?' : 'Delete this campaign?')) return;
        try {
            const res = await fetch(`/api/ads/campaigns/${campaignId}`, { method: 'DELETE' });
            if (res.ok) fetchData();
        } catch (err) {
            console.error('Delete campaign failed:', err);
        }
    };

    const statusColors: Record<string, string> = {
        active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
        ended: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    };

    const tabs = [
        { id: 'overview' as const, label: t('overview'), icon: 'dashboard' },
        { id: 'campaigns' as const, label: t('campaigns'), icon: 'campaign' },
        { id: 'ads' as const, label: t('ads'), icon: 'ad_units' },
        { id: 'creatives' as const, label: t('creatives'), icon: 'brush' },
        { id: 'settings' as const, label: t('settings'), icon: 'settings' },
    ];

    // ── Canvas Drawing with Layer Support ──
    const drawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const sizeInfo = BANNER_SIZES.find(s => s.id === editCreative.size) || BANNER_SIZES[0];
        const scale = Math.min(1, 400 / sizeInfo.w, 300 / sizeInfo.h);
        canvas.width = sizeInfo.w * scale;
        canvas.height = sizeInfo.h * scale;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const getLayerState = (id: string) => layers.find(l => l.id === id) || { visible: true, opacity: 100 };

        // Background layer
        const bgLayer = getLayerState('background');
        if (bgLayer.visible) {
            ctx.globalAlpha = bgLayer.opacity / 100;
            ctx.fillStyle = editCreative.bgColor || '#1e3a5f';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        // Image layer
        const imgLayer = getLayerState('image');
        if (imgLayer.visible && bgImageRef.current && bgImageRef.current.complete) {
            ctx.globalAlpha = imgLayer.opacity / 100;
            ctx.drawImage(bgImageRef.current, 0, 0, canvas.width, canvas.height);
            // Dark overlay for text readability
            ctx.fillStyle = 'rgba(0,0,0,0.35)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.globalAlpha = 1;

        // Headline layer
        const headlineLayer = getLayerState('headline');
        if (headlineLayer.visible && editCreative.headline) {
            ctx.globalAlpha = headlineLayer.opacity / 100;
            const headlineSize = Math.max(12, Math.floor(canvas.height * 0.15));
            ctx.fillStyle = editCreative.textColor || '#ffffff';
            ctx.font = `bold ${headlineSize}px Arial`;
            ctx.textAlign = 'center';
            ctx.fillText(editCreative.headline, canvas.width / 2, canvas.height * 0.35, canvas.width - 20);
        }

        // Description layer
        const descLayer = getLayerState('description');
        if (descLayer.visible && editCreative.description) {
            ctx.globalAlpha = descLayer.opacity / 100;
            const descSize = Math.max(9, Math.floor(canvas.height * 0.08));
            ctx.fillStyle = editCreative.textColor || '#ffffff';
            ctx.font = `${descSize}px Arial`;
            ctx.textAlign = 'center';
            ctx.fillText(editCreative.description, canvas.width / 2, canvas.height * 0.52, canvas.width - 20);
        }
        ctx.globalAlpha = 1;

        // CTA Button layer
        const ctaLayer = getLayerState('cta');
        if (ctaLayer.visible && editCreative.ctaText) {
            ctx.globalAlpha = ctaLayer.opacity / 100;
            const ctaW = Math.min(canvas.width * 0.5, 160 * scale);
            const ctaH = Math.max(20, canvas.height * 0.15);
            const ctaX = (canvas.width - ctaW) / 2;
            const ctaY = canvas.height * 0.65;
            ctx.fillStyle = editCreative.ctaColor || '#f59e0b';
            ctx.beginPath();
            const r = 4 * scale;
            ctx.moveTo(ctaX + r, ctaY);
            ctx.arcTo(ctaX + ctaW, ctaY, ctaX + ctaW, ctaY + ctaH, r);
            ctx.arcTo(ctaX + ctaW, ctaY + ctaH, ctaX, ctaY + ctaH, r);
            ctx.arcTo(ctaX, ctaY + ctaH, ctaX, ctaY, r);
            ctx.arcTo(ctaX, ctaY, ctaX + ctaW, ctaY, r);
            ctx.fill();

            const ctaFontSize = Math.max(9, Math.floor(ctaH * 0.5));
            ctx.fillStyle = '#ffffff';
            ctx.font = `bold ${ctaFontSize}px Arial`;
            ctx.fillText(editCreative.ctaText, canvas.width / 2, ctaY + ctaH * 0.65, ctaW - 10);
        }
        ctx.globalAlpha = 1;
    }, [editCreative, layers]);

    useEffect(() => {
        if (showEditor) drawCanvas();
    }, [showEditor, drawCanvas]);

    // Load background image when URL changes
    useEffect(() => {
        if (editCreative.imageUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => { bgImageRef.current = img; drawCanvas(); };
            img.onerror = () => { bgImageRef.current = null; drawCanvas(); };
            img.src = editCreative.imageUrl;
        } else {
            bgImageRef.current = null;
            if (showEditor) drawCanvas();
        }
    }, [editCreative.imageUrl]);

    // ── AI Generate Text ──
    const handleAIGenerate = async (field: 'headline' | 'description' | 'both') => {
        setAiGenerating(field);
        try {
            const prompt = field === 'headline'
                ? `You are a travel marketing expert. Generate a short, punchy headline (max 8 words) for a travel ad banner. The ad is for ${editCreative.headline || 'a premium vacation'}. Return ONLY the headline text, nothing else.`
                : field === 'description'
                    ? `You are a travel marketing expert. Generate a brief description (max 15 words) for a travel ad banner with headline: "${editCreative.headline}". Return ONLY the description, nothing else.`
                    : `You are a travel marketing expert. Generate ad text for a travel banner in this exact JSON format: {"headline": "...", "description": "..."}. Headline max 8 words. Description max 15 words. Topic: premium travel. Return ONLY the JSON.`;

            const res = await fetch('/api/ai-config/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt,
                    variables: {},
                    model: { provider: 'gemini', model: 'gemini-2.0-flash', temperature: 0.8, maxTokens: 100 }
                })
            });
            const data = await res.json();
            if (data.success && data.output) {
                if (field === 'both') {
                    try {
                        const parsed = JSON.parse(data.output.trim());
                        setEditCreative(p => ({ ...p, headline: parsed.headline || p.headline, description: parsed.description || p.description }));
                    } catch {
                        setEditCreative(p => ({ ...p, headline: data.output.trim() }));
                    }
                } else {
                    const cleanText = data.output.trim().replace(/^["']|["']$/g, '');
                    setEditCreative(p => ({ ...p, [field]: cleanText }));
                }
            }
        } catch (err) { console.error('AI generate failed:', err); }
        finally { setAiGenerating(null); }
    };

    // ── Stock Image Search ──
    const handleStockSearch = async () => {
        if (!stockQuery.trim()) return;
        setStockLoading(true);
        try {
            const res = await fetch(`/api/custom-content/images/search?query=${encodeURIComponent(stockQuery)}&count=12`);
            const data = await res.json();
            if (data.success && data.images) {
                setStockResults(data.images.map((img: any) => ({
                    url: img.url || img.largeImageURL || img.src?.large || img.urls?.regular || img.link,
                    thumb: img.thumbnail || img.previewURL || img.src?.tiny || img.urls?.thumb || img.link,
                    alt: img.alt || img.tags || img.description || 'Stock image'
                })));
            } else {
                setStockResults([]);
            }
        } catch (err) {
            console.error('Stock search failed:', err);
            setStockResults([]);
        } finally { setStockLoading(false); }
    };

    // ── Layer Toggle ──
    const toggleLayerVisibility = (layerId: string) => {
        setLayers(prev => prev.map(l => l.id === layerId ? { ...l, visible: !l.visible } : l));
    };
    const setLayerOpacity = (layerId: string, opacity: number) => {
        setLayers(prev => prev.map(l => l.id === layerId ? { ...l, opacity } : l));
    };

    const handleSaveCreative = async () => {
        if (!editCreative.name?.trim()) return;
        try {
            const res = await fetch('/api/ads/creatives', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editCreative)
            });
            if (res.ok) {
                const data = await res.json();
                setCreatives(prev => [data.creative, ...prev]);
                setShowEditor(false);
            }
        } catch (err) { console.error('Save creative failed:', err); }
    };

    const handleDeleteCreative = async (id: string) => {
        if (!confirm(language === 'sv' ? 'Radera detta annonsmaterial?' : 'Delete this creative?')) return;
        try {
            const res = await fetch(`/api/ads/creatives/${id}`, { method: 'DELETE' });
            if (res.ok) setCreatives(prev => prev.filter(c => c.id !== id));
        } catch (err) { console.error('Delete creative failed:', err); }
    };

    const handleExportPNG = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `${editCreative.name || 'banner'}_${editCreative.size}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        setSettingsMsg(null);
        try {
            const res = await fetch('/api/ads/settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ settings: platformSettings })
            });
            if (res.ok) setSettingsMsg(t('settings_saved'));
        } catch (err) { console.error('Save settings failed:', err); }
        finally { setSavingSettings(false); }
    };

    // ── Loading State ──
    if (loading) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">{t('loading')}</p>
                </div>
            </div>
        );
    }

    // ── Error State ──
    if (error) {
        return (
            <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <span className="material-icons-round text-4xl text-red-400 mb-3 block">warning</span>
                    <p className="text-gray-600 dark:text-gray-400 mb-3">{t('error_loading')}</p>
                    <button onClick={fetchData} className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-teal-600 transition-colors">
                        {t('retry')}
                    </button>
                </div>
            </div>
        );
    }

    const totalImpressions = summary?.totalImpressions || ads.reduce((s, a) => s + (a.metrics?.impressions || 0), 0);
    const totalClicks = summary?.totalClicks || ads.reduce((s, a) => s + (a.metrics?.clicks || 0), 0);
    const totalConversions = summary?.totalConversions || ads.reduce((s, a) => s + (a.metrics?.conversions || 0), 0);
    const totalRevenue = summary?.totalRevenue || 0;
    const ctr = summary?.averageCTR || (totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00');
    const activeCampaigns = campaigns.filter(c => c.status === 'active').length;

    // ── Overview Tab ──
    const renderOverview = () => (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                    { label: t('total_campaigns'), value: campaigns.length.toString(), icon: 'campaign', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
                    { label: t('active_campaigns'), value: activeCampaigns.toString(), icon: 'play_circle', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' },
                    { label: t('impressions'), value: totalImpressions.toLocaleString(), icon: 'visibility', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' },
                    { label: t('clicks'), value: totalClicks.toLocaleString(), icon: 'touch_app', color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20' },
                    { label: t('conversions'), value: totalConversions.toString(), icon: 'shopping_cart', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20' },
                    { label: t('ctr'), value: `${ctr}%`, icon: 'percent', color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-900/20' },
                ].map((stat, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <div className={`w-8 h-8 rounded-lg ${stat.bg} flex items-center justify-center`}>
                                <span className={`material-icons-round text-base ${stat.color}`}>{stat.icon}</span>
                            </div>
                        </div>
                        <div className="text-xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* Recent Campaigns */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <span className="material-icons-round text-lg text-primary">campaign</span>
                    {t('campaigns')}
                    <span className="ml-auto text-xs text-gray-400 font-normal">{campaigns.length} {t('total_campaigns').toLowerCase()}</span>
                </h3>
                {campaigns.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                        <span className="material-icons-round text-4xl mb-2 block">campaign</span>
                        <p className="text-sm">{t('no_campaigns')}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="pb-2 font-medium">{t('name')}</th>
                                    <th className="pb-2 font-medium">{t('type')}</th>
                                    <th className="pb-2 font-medium">{t('status')}</th>
                                    <th className="pb-2 font-medium text-right">{t('impressions')}</th>
                                    <th className="pb-2 font-medium text-right">{t('clicks')}</th>
                                    <th className="pb-2 font-medium text-right">{t('conversions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.slice(0, 10).map(c => (
                                    <tr key={c._id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                                        <td className="py-2.5 font-medium text-gray-800 dark:text-gray-200">{c.name}</td>
                                        <td className="py-2.5 text-gray-600 dark:text-gray-400 text-xs">{t(c.campaignType) || c.campaignType}</td>
                                        <td className="py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[c.status] || 'bg-gray-100 text-gray-600'}`}>{t(c.status)}</span></td>
                                        <td className="py-2.5 text-right text-gray-700 dark:text-gray-300">{(c.metrics?.impressions || 0).toLocaleString()}</td>
                                        <td className="py-2.5 text-right text-gray-700 dark:text-gray-300">{(c.metrics?.clicks || 0).toLocaleString()}</td>
                                        <td className="py-2.5 text-right text-gray-700 dark:text-gray-300">{c.metrics?.conversions || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Top Performing Ads */}
            {summary?.topPerformingAds && summary.topPerformingAds.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                        <span className="material-icons-round text-lg text-amber-500">stars</span>
                        {t('top_performing')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {summary.topPerformingAds.slice(0, 6).map((ad: any, i: number) => (
                            <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                <h4 className="font-medium text-gray-800 dark:text-gray-200 text-sm truncate">{ad.name}</h4>
                                <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                                    <div><div className="text-sm font-bold text-gray-900 dark:text-white">{(ad.impressions || 0).toLocaleString()}</div><div className="text-[10px] text-gray-400">{t('impressions')}</div></div>
                                    <div><div className="text-sm font-bold text-gray-900 dark:text-white">{(ad.clicks || 0).toLocaleString()}</div><div className="text-[10px] text-gray-400">{t('clicks')}</div></div>
                                    <div><div className="text-sm font-bold text-gray-900 dark:text-white">{ad.conversions || 0}</div><div className="text-[10px] text-gray-400">{t('conversions')}</div></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    // ── Campaigns Tab ──
    const renderCampaigns = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('campaigns')}</h3>
                <button className="px-4 py-2 bg-primary hover:bg-teal-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 shadow-lg shadow-primary/25">
                    <span className="material-icons-round text-sm">add</span>
                    {t('create_campaign')}
                </button>
            </div>
            {campaigns.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <span className="material-icons-round text-5xl text-gray-300 dark:text-gray-600 mb-3 block">campaign</span>
                    <p className="text-gray-500 dark:text-gray-400">{t('no_campaigns')}</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {campaigns.map(c => (
                        <div key={c._id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:border-primary/30 dark:hover:border-primary/30 transition-all">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white">{c.name}</h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {t(c.campaignType) || c.campaignType} · {c.platform || t('internal')}
                                        {c.startDate && ` · ${new Date(c.startDate).toLocaleDateString()}`}
                                        {c.endDate && ` → ${new Date(c.endDate).toLocaleDateString()}`}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColors[c.status] || 'bg-gray-100 text-gray-600'}`}>{t(c.status)}</span>
                                    <div className="flex gap-1">
                                        {c.status === 'active' ? (
                                            <button onClick={() => handleStatusChange(c._id, 'paused')} className="p-1 text-gray-400 hover:text-amber-500 transition-colors" title={t('pause')}>
                                                <span className="material-icons-round text-sm">pause</span>
                                            </button>
                                        ) : c.status === 'paused' ? (
                                            <button onClick={() => handleStatusChange(c._id, 'active')} className="p-1 text-gray-400 hover:text-green-500 transition-colors" title={t('activate')}>
                                                <span className="material-icons-round text-sm">play_arrow</span>
                                            </button>
                                        ) : null}
                                        <button onClick={() => handleDeleteCampaign(c._id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title={t('delete')}>
                                            <span className="material-icons-round text-sm">delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {/* Metrics */}
                            <div className="grid grid-cols-3 gap-3 text-center">
                                <div><div className="text-sm font-bold text-gray-900 dark:text-white">{(c.metrics?.impressions || 0).toLocaleString()}</div><div className="text-xs text-gray-500 dark:text-gray-400">{t('impressions')}</div></div>
                                <div><div className="text-sm font-bold text-gray-900 dark:text-white">{(c.metrics?.clicks || 0).toLocaleString()}</div><div className="text-xs text-gray-500 dark:text-gray-400">{t('clicks')}</div></div>
                                <div><div className="text-sm font-bold text-gray-900 dark:text-white">{c.metrics?.conversions || 0}</div><div className="text-xs text-gray-500 dark:text-gray-400">{t('conversions')}</div></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ── Ads Tab ──
    const renderAds = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('ads')}</h3>
                <button className="px-4 py-2 bg-primary hover:bg-teal-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 shadow-lg shadow-primary/25">
                    <span className="material-icons-round text-sm">add</span>
                    {t('create_ad')}
                </button>
            </div>
            {ads.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
                    <span className="material-icons-round text-5xl text-gray-300 dark:text-gray-600 mb-3 block">ad_units</span>
                    <p className="text-gray-500 dark:text-gray-400">{t('no_ads')}</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="p-3 font-medium">{t('name')}</th>
                                    <th className="p-3 font-medium">{t('type')}</th>
                                    <th className="p-3 font-medium">{t('status')}</th>
                                    <th className="p-3 font-medium text-right">{t('impressions')}</th>
                                    <th className="p-3 font-medium text-right">{t('clicks')}</th>
                                    <th className="p-3 font-medium text-right">{t('conversions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ads.map(ad => (
                                    <tr key={ad._id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                                        <td className="p-3 font-medium text-gray-800 dark:text-gray-200">{ad.name}</td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400 text-xs capitalize">{ad.type?.replace(/_/g, ' ')}</td>
                                        <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[ad.status] || 'bg-gray-100 text-gray-600'}`}>{t(ad.status) || ad.status}</span></td>
                                        <td className="p-3 text-right text-gray-700 dark:text-gray-300">{(ad.metrics?.impressions || 0).toLocaleString()}</td>
                                        <td className="p-3 text-right text-gray-700 dark:text-gray-300">{(ad.metrics?.clicks || 0).toLocaleString()}</td>
                                        <td className="p-3 text-right text-gray-700 dark:text-gray-300">{ad.metrics?.conversions || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );


    // ── Creatives Tab ──
    const renderCreatives = () => (
        <div className="space-y-4">
            {/* Action Bar */}
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('creatives')}</h3>
                <button onClick={() => { setShowEditor(true); setEditCreative({ name: '', size: '300x250', template: 'travel-promo', headline: 'Dream Vacation Awaits', ctaText: 'Book Now', description: 'Premium travel packages', bgColor: '#1e3a5f', textColor: '#ffffff', ctaColor: '#f59e0b', imageUrl: '' }); }}
                    className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors flex items-center gap-2">
                    <span className="material-icons-round text-lg">add</span>{t('new_creative')}
                </button>
            </div>

            {/* Editor Modal */}
            {showEditor && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left: Controls */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('name')}</label>
                                <input value={editCreative.name || ''} onChange={e => setEditCreative(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white" placeholder="Banner name" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('size')}</label>
                                    <select value={editCreative.size} onChange={e => setEditCreative(p => ({ ...p, size: e.target.value }))}
                                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white">
                                        {BANNER_SIZES.map(s => <option key={s.id} value={s.id}>{s.label} ({s.id})</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('template')}</label>
                                    <select value={editCreative.template} onChange={e => { const tpl = TEMPLATES.find(t => t.id === e.target.value); setEditCreative(p => ({ ...p, template: e.target.value, bgColor: tpl?.bg || p.bgColor, textColor: tpl?.text || p.textColor, ctaColor: tpl?.cta || p.ctaColor })); }}
                                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white">
                                        {TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                </div>
                            </div>

                            {/* Headline + AI Generate */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('headline')}</label>
                                    <button
                                        onClick={() => handleAIGenerate('headline')}
                                        disabled={!!aiGenerating}
                                        className="text-xs px-2 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-md font-medium hover:from-purple-600 hover:to-blue-600 disabled:opacity-50 flex items-center gap-1 transition-all"
                                    >
                                        <span className={`material-icons-round text-xs ${aiGenerating === 'headline' ? 'animate-spin' : ''}`}>
                                            {aiGenerating === 'headline' ? 'refresh' : 'auto_awesome'}
                                        </span>
                                        {aiGenerating === 'headline' ? t('ai_generating') : t('ai_generate')}
                                    </button>
                                </div>
                                <input value={editCreative.headline || ''} onChange={e => setEditCreative(p => ({ ...p, headline: e.target.value }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white" />
                            </div>

                            {/* Description + AI Generate */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('description')}</label>
                                    <button
                                        onClick={() => handleAIGenerate('description')}
                                        disabled={!!aiGenerating}
                                        className="text-xs px-2 py-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-md font-medium hover:from-purple-600 hover:to-blue-600 disabled:opacity-50 flex items-center gap-1 transition-all"
                                    >
                                        <span className={`material-icons-round text-xs ${aiGenerating === 'description' ? 'animate-spin' : ''}`}>
                                            {aiGenerating === 'description' ? 'refresh' : 'auto_awesome'}
                                        </span>
                                        {aiGenerating === 'description' ? t('ai_generating') : t('ai_generate')}
                                    </button>
                                </div>
                                <input value={editCreative.description || ''} onChange={e => setEditCreative(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white" />
                            </div>

                            {/* AI Generate Both */}
                            <button
                                onClick={() => handleAIGenerate('both')}
                                disabled={!!aiGenerating}
                                className="w-full px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-lg shadow-purple-500/20"
                            >
                                <span className={`material-icons-round text-base ${aiGenerating === 'both' ? 'animate-spin' : ''}`}>
                                    {aiGenerating === 'both' ? 'refresh' : 'auto_awesome'}
                                </span>
                                {aiGenerating === 'both' ? t('ai_generating') : `✨ ${t('ai_generate')} — Headline + Description`}
                            </button>

                            <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('cta_text')}</label>
                                <input value={editCreative.ctaText || ''} onChange={e => setEditCreative(p => ({ ...p, ctaText: e.target.value }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white" />
                            </div>

                            {/* Background Image / Stock Search */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">{t('bg_image')}</label>
                                    <div className="flex items-center gap-1">
                                        {editCreative.imageUrl && (
                                            <button onClick={() => setEditCreative(p => ({ ...p, imageUrl: '' }))} className="text-xs px-2 py-1 bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-md font-medium hover:bg-red-200 transition-colors">
                                                {t('remove_image')}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setShowStockSearch(!showStockSearch)}
                                            className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-md font-medium hover:bg-emerald-200 dark:hover:bg-emerald-900/50 flex items-center gap-1 transition-colors"
                                        >
                                            <span className="material-icons-round text-xs">image_search</span>
                                            {t('stock_search')}
                                        </button>
                                    </div>
                                </div>
                                <input value={editCreative.imageUrl || ''} onChange={e => setEditCreative(p => ({ ...p, imageUrl: e.target.value }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white" placeholder="https://example.com/image.jpg" />

                                {/* Stock Image Search Panel */}
                                {showStockSearch && (
                                    <div className="mt-2 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                        <div className="flex gap-2 mb-3">
                                            <input
                                                value={stockQuery}
                                                onChange={e => setStockQuery(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && handleStockSearch()}
                                                placeholder={t('stock_search_placeholder')}
                                                className="flex-1 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white"
                                            />
                                            <button onClick={handleStockSearch} disabled={stockLoading} className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-teal-600 disabled:opacity-50 flex items-center gap-1">
                                                <span className={`material-icons-round text-sm ${stockLoading ? 'animate-spin' : ''}`}>{stockLoading ? 'refresh' : 'search'}</span>
                                            </button>
                                        </div>
                                        {stockResults.length > 0 && (
                                            <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                                                {stockResults.map((img, i) => (
                                                    <button key={i} onClick={() => { setEditCreative(p => ({ ...p, imageUrl: img.url })); setShowStockSearch(false); }}
                                                        className="relative group rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-colors">
                                                        <img src={img.thumb} alt={img.alt} className="w-full h-16 object-cover" />
                                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                            <span className="material-icons-round text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity">check_circle</span>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Colors */}
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('bg_color')}</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={editCreative.bgColor || '#1e3a5f'} onChange={e => setEditCreative(p => ({ ...p, bgColor: e.target.value }))} className="w-8 h-8 rounded cursor-pointer border-0" />
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{editCreative.bgColor}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('text_color')}</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={editCreative.textColor || '#ffffff'} onChange={e => setEditCreative(p => ({ ...p, textColor: e.target.value }))} className="w-8 h-8 rounded cursor-pointer border-0" />
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{editCreative.textColor}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('cta_color')}</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={editCreative.ctaColor || '#f59e0b'} onChange={e => setEditCreative(p => ({ ...p, ctaColor: e.target.value }))} className="w-8 h-8 rounded cursor-pointer border-0" />
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{editCreative.ctaColor}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Layer Panel */}
                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="material-icons-round text-sm text-gray-500">layers</span>
                                    <span className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase">{t('layers')}</span>
                                </div>
                                <div className="space-y-1.5">
                                    {layers.map(layer => (
                                        <div key={layer.id} className="flex items-center gap-2 group">
                                            <button onClick={() => toggleLayerVisibility(layer.id)}
                                                className={`p-0.5 rounded transition-colors ${layer.visible ? 'text-green-500' : 'text-gray-400'}`}
                                                title={layer.visible ? t('layer_visible') : t('layer_hidden')}>
                                                <span className="material-icons-round text-sm">{layer.visible ? 'visibility' : 'visibility_off'}</span>
                                            </button>
                                            <span className="text-xs text-gray-700 dark:text-gray-300 flex-1 font-medium">{layer.name}</span>
                                            <input type="range" min="0" max="100" value={layer.opacity}
                                                onChange={e => setLayerOpacity(layer.id, parseInt(e.target.value))}
                                                className="w-16 h-1 accent-primary opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" />
                                            <span className="text-[10px] text-gray-400 w-8 text-right opacity-0 group-hover:opacity-100 transition-opacity">{layer.opacity}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right: Canvas Preview */}
                        <div className="flex flex-col items-center">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{t('preview')}</label>
                            <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-900 inline-flex items-center justify-center">
                                <canvas ref={canvasRef} className="rounded shadow-lg" />
                            </div>
                            <div className="flex gap-2 mt-4 w-full">
                                <button onClick={handleExportPNG} className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-1">
                                    <span className="material-icons-round text-base">download</span>{t('export_png')}
                                </button>
                                <button onClick={() => setShowEditor(false)} className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                    {t('cancel')}
                                </button>
                                <button onClick={handleSaveCreative} className="flex-1 px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors flex items-center justify-center gap-1">
                                    <span className="material-icons-round text-base">save</span>{t('save_creative')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Saved Creatives Grid */}
            <div>
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">{t('saved_creatives')} ({creatives.length})</h4>
                {creatives.length === 0 ? (
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-10 text-center">
                        <span className="material-icons-round text-4xl text-gray-300 dark:text-gray-600 mb-2 block">palette</span>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('no_creatives')}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {creatives.map(c => (
                            <div key={c.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden group hover:shadow-lg transition-shadow">
                                <div className="h-32 flex items-center justify-center" style={{ backgroundColor: c.bgColor }}>
                                    <div className="text-center px-3">
                                        <p className="font-bold text-sm" style={{ color: c.textColor }}>{c.headline}</p>
                                        <p className="text-xs mt-1 opacity-80" style={{ color: c.textColor }}>{c.description}</p>
                                        {c.ctaText && <span className="inline-block mt-2 px-3 py-1 rounded text-xs font-bold text-white" style={{ backgroundColor: c.ctaColor }}>{c.ctaText}</span>}
                                    </div>
                                </div>
                                <div className="p-3 flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">{c.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{c.size} · {TEMPLATES.find(t => t.id === c.template)?.name || c.template}</p>
                                    </div>
                                    <button onClick={() => handleDeleteCreative(c.id)} className="p-1.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                                        <span className="material-icons-round text-lg">delete</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    // ── Settings Tab ──
    const renderSettings = () => {
        const platforms = [
            {
                key: 'google' as const, name: 'Google Ads', icon: '🔍', color: 'bg-blue-50 dark:bg-blue-900/20', fields: [
                    { key: 'clientId', label: t('client_id') }, { key: 'apiKey', label: t('api_key') }, { key: 'accountId', label: t('account_id') }
                ]
            },
            {
                key: 'meta' as const, name: 'Meta Ads', icon: '📘', color: 'bg-indigo-50 dark:bg-indigo-900/20', fields: [
                    { key: 'appId', label: t('app_id') }, { key: 'appSecret', label: t('app_secret') }, { key: 'adAccountId', label: t('account_id') }
                ]
            },
            {
                key: 'tiktok' as const, name: 'TikTok Ads', icon: '🎵', color: 'bg-pink-50 dark:bg-pink-900/20', fields: [
                    { key: 'appId', label: t('app_id') }, { key: 'secret', label: t('client_secret') }, { key: 'advertiserId', label: t('advertiser_id') }
                ]
            },
            {
                key: 'microsoft' as const, name: 'Microsoft Ads', icon: '🪟', color: 'bg-cyan-50 dark:bg-cyan-900/20', fields: [
                    { key: 'clientId', label: t('client_id') }, { key: 'clientSecret', label: t('client_secret') }, { key: 'accountId', label: t('account_id') }
                ]
            },
        ];

        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('platform_settings')}</h3>
                    <button onClick={handleSaveSettings} disabled={savingSettings}
                        className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors flex items-center gap-2 disabled:opacity-50">
                        <span className="material-icons-round text-lg">{savingSettings ? 'hourglass_empty' : 'save'}</span>
                        {t('save_settings')}
                    </button>
                </div>
                {settingsMsg && <div className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-4 py-2 rounded-lg text-sm flex items-center gap-2"><span className="material-icons-round text-lg">check_circle</span>{settingsMsg}</div>}

                {platforms.map(p => (
                    <div key={p.key} className={`${p.color} rounded-xl border border-gray-200 dark:border-gray-700 p-5`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">{p.icon}</span>
                                <div>
                                    <h4 className="font-semibold text-gray-900 dark:text-white">{p.name}</h4>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${platformSettings[p.key]?.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                        {platformSettings[p.key]?.enabled ? t('connected') : t('disconnected')}
                                    </span>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={platformSettings[p.key]?.enabled || false}
                                    onChange={e => setPlatformSettings(prev => ({ ...prev, [p.key]: { ...prev[p.key], enabled: e.target.checked, status: e.target.checked ? 'connected' : 'disconnected' } }))}
                                    className="sr-only peer" />
                                <div className="w-10 h-5 bg-gray-300 peer-checked:bg-primary rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
                            </label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {p.fields.map(f => (
                                <div key={f.key}>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{f.label}</label>
                                    <input type="password" value={(platformSettings[p.key] as any)?.[f.key] || ''}
                                        onChange={e => setPlatformSettings(prev => ({ ...prev, [p.key]: { ...prev[p.key], [f.key]: e.target.value } }))}
                                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white"
                                        placeholder={`Enter ${f.label}`} />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="h-full flex flex-col">
            {/* Header with tabs */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center gap-4">
                <div className="flex gap-2 overflow-x-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id
                                ? 'bg-primary text-white shadow-lg shadow-primary/25'
                                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                                }`}
                        >
                            <span className="material-icons-round text-lg">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <button onClick={fetchData} className="p-2 text-gray-400 hover:text-primary transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700" title="Refresh">
                        <span className="material-icons-round text-lg">refresh</span>
                    </button>
                    <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap hidden md:block">
                        {t('this_month')}
                    </span>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gray-50 dark:bg-gray-900">
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'campaigns' && renderCampaigns()}
                {activeTab === 'ads' && renderAds()}
                {activeTab === 'creatives' && renderCreatives()}
                {activeTab === 'settings' && renderSettings()}
            </div>
        </div>
    );
};

export default AdManagementContent;
